<template>
  <div id="project-wrapper" class="wdw-widget-project" ref="projectWrapper">
    <div class="row">
      <!-- feedbackMessage -->
      <div v-if="feedbackMessage.message !== ''">
        <Message :severity="feedbackMessageSeverity">{{ feedbackMessage.message }}</Message>
      </div>
      <template v-if="showThought">
        <div id="topic-dynamic" class="thought-detail-wrapper show">
          <div v-show="showDetailedThought && !showThoughtForm">
            <!-- thought-detail-->
            <thought-wrapper
              :thoughtid="openedThoughtId"
              :config="wrapperConfig"
              :actions="wrapperActions"
              @close-thought="closeThought"
              @edit-thought="editThought"
            />
          </div>
          <!-- thought-form -->
          <div v-if="showThoughtForm">
            <thought-form-choose-topic
              v-if="topicsForThoughts.length > 1 && !openedThoughtId"
              :avalibleTopics="topicsForThoughts"
              @topic-selected="selectedTopicForForm"
            />

            <thought-form
              v-if="topicForThoughtForm"
              :thoughtid="openedThoughtId"
              :topic="topicForThoughtForm"
              @open-thought="openThought"
              @feedback-thought="onFeedbackFromThought"
              @close-thought="closeThought"
              @cancel-thought-form="cancelThoughtForm"
              :labelTitle="thoughtFormTitleLabel"
              :labelDescription="thoughtFormDescriptionLabel"
            />
          </div>
        </div>
      </template>
      <!-- Project -->
      <div id="project-static">
        <template v-if="project && projectConfigLoaded">
          <div v-show="!showThought">
            <!-- filter -->
            <div class="thought-filter" v-if="topics.length > 0">
              <thought-filter
                :fields="defaultFilterFields"
                :attributeFields="attributeFilterFields"
                :filterAttributesDependency="filterAttributesDependency"
                :watermark="true"
              />
            </div>
            <!-- create new thought -->
            <wdw-dynbox v-if="canCreateThoughtAnyTopic || canCreateThoughtAnyTopicAfterLogin">
              <div class="create-button">
                <button type="button" class="btn btn-primary btn-create-thought" @click="openCreateThoughtForm">
                  {{ domainStore.translate('thought.create.btn-title') }}
                </button>
                <OverlayPanel
                  v-if="!isLoggedIn"
                  ref="loginOP"
                  :showCloseIcon="true"
                  :dismissable="true"
                  appendTo="body"
                  class="wdw-widgets"
                >
                  <p>{{ domainStore.translate('thought.create.requires-login') }}</p>
                  <Login :domainid="domainid" />
                </OverlayPanel>
              </div>
            </wdw-dynbox>
            <!-- thought-list -->
            <div class="thought-list" v-if="!showThought">
              <thought-list
                @open-thought="openThought"
                @edit-thought="editThought"
                :config="{
                  hideVoting: false,
                  hideComments: false,
                  hideCommentButton: false,
                  hideFeedback: false,
                }"
              />
            </div>
          </div>
        </template>
        <template v-else>project not found</template>
      </div>
    </div>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import { projectStore } from '@/store/project-store';
import { thoughtStore } from '@/store/thought-store';
import { filterStore } from '@/store/filter-store';
import useAuthentication from '@/composables/user/useAuthentication';
import { onBeforeMount, ref, watch, computed, nextTick, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import ThoughtFilter from '@/components/discuss/topic/ThoughtFilter.vue';
import Login from '@/components/user/Login.vue';
import OverlayPanel from 'primevue/overlaypanel';
import Message from 'primevue/message';
import ThoughtList from '@/components/discuss/topic/ThoughtList.vue';
import ThoughtWrapper from '@/components/discuss/thought/Wrapper.vue';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import useThoughtFilter from '@/composables/thought/useThoughtFilter';
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useHashParams from '@/composables/general/useHashParams';
import useConverter from '@/composables/general/useConverter';
import useTopicTerm from '@/composables/topic/useTopicTerm';
import ThoughtForm from '@/components/discuss/thought/Form.vue';
import ThoughtFormChooseTopic from '@/components/discuss/thought/FormChooseTopic.vue';

export default {
  name: 'Project',
  props: {
    domainid: {
      type: Number,
      required: true,
    },
    projectid: {
      type: Number,
      required: true,
    },
  },

  setup: function (props) {
    const route = useRoute();
    const { getVirtualUser } = useAuthentication();
    const { deleteHashParam, setHashParams } = useHashParams(route);
    const { getDefaultFilterFields, getSortOptions, getThoughtWeightField } = useThoughtFilter();
    const { canCreateThought, canCreateThoughtAfterLogin } = useTopicConfig();
    const { toBool } = useConverter();
    const { topicLabel } = useTopicTerm();

    // VARIABLES
    const topics = ref([]); // list of topics for topic filter
    const defaultFilterFields = ref({}); // Default filter fields
    const attributeFilterFields = ref([]); // Attribute filter fields depending on topics
    const filterAttributesDependency = ref({}); // Which attribute filter goes to which topic
    const feedbackMessage = reactive({ message: '', type: '' }); // Feedback
    const openedThoughtId = ref(null); // Id of opened thought
    const showThought = ref(false); // whether or not we can show the thought
    const scrollPosition = ref(null);
    const showThoughtForm = ref(false); // whether or not we can show the form for thought creation
    const topicForThoughtForm = ref(null);
    const loginOP = ref();
    const projectWrapper = ref(HTMLElement | null);
    const initialTopicPicked = ref(false);
    const projectConfigLoaded = ref(false);
    // const topicsForThoughts = ref ([]);
    const wrapperActions = ref({});
    // T18783: filter by chosen topicid thought hash parameter
    const filteredTopicIdFromHash = ref(0);

    // VARIABLE INIT
    // add default projectid parameter to filter.
    defaultFilterFields.value = getDefaultFilterFields();
    defaultFilterFields.value.projectid = {
      name: 'Projekt',
      type: 'number',
      default_value: props.projectid,
      visible: false,
    };

    // COMPUTED
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const domain = computed(() => {
      return domainStore.getState().domain;
    });
    const wrapperConfig = computed(() => {
      return {
        showMapPreview: true,
      };
    });
    const thoughtFormTitleLabel = computed(() => {
      return project.value
        ? project.value.thought_title_label
        : domainStore.translate('thought.create.form.title-label');
    });
    const thoughtFormDescriptionLabel = computed(() => {
      return project.value
        ? project.value.thought_content_label
        : domainStore.translate('thought.create.form.description-label');
    });
    const feedbackMessageSeverity = computed(() => {
      return feedbackMessage.type || 'info';
    });

    const showDetailedThought = computed(() => {
      return Boolean(showThought.value && openedThoughtId.value);
    });
    // true if user can create thought in at least 1 topic
    const canCreateThoughtAnyTopic = computed(() => {
      if (topics.value.length == 0) return false;
      return topics.value.some(el => canCreateThought(el));
    });
    // true if user can create thought in at least 1 topic after logging in
    const canCreateThoughtAnyTopicAfterLogin = computed(() => {
      if (topics.value.length == 0) return false;
      return topics.value.some(el => canCreateThoughtAfterLogin(el));
    });

    //if only one topc is avalibe, Assaign current Topic for Componenet to Render.
    //computed variable that filters topics into the avalivle topic to writea thought for.
    const topicsForThoughts = computed(() => {
      return topics.value.filter(topic => canCreateThought(topic));
    });

    // METHODS
    function doShowThoughtForm(topicid) {
      showThoughtForm.value = true;
      if (topicid) {
        topicForThoughtForm.value = topicsForThoughts.value.filter(t => t.id == topicid)[0];
      } else if (topicsForThoughts.value.length == 1) {
        topicForThoughtForm.value = topicsForThoughts.value[0];
      }
    }

    function clearFeedback() {
      feedbackMessage.message = '';
      feedbackMessage.type = '';
    }

    function confirmClosingForm() {
      return confirm('Möchten Sie die Eingabe abbrechen?');
    }

    function openThought(thoughtid, topicid, actions) {
      if (actions) {
        wrapperActions.value = actions;
      } else {
        wrapperActions.value = {};
      }
      showThoughtForm.value = false; // Disables the form also
      topicForThoughtForm.value = null;
      openedThoughtId.value = thoughtid;
      showThought.value = true;
      scrollPosition.value = window.pageYOffset;
      // Why false? See MapProject.vue in the same method
      setHashParams({ thought_id: thoughtid }, false);
      // Async call needed in order to wait for the DOM element to display
      nextTick(function () {
        projectWrapper.value.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      });
    }

    function editThought(thoughtid, topicid) {
      openedThoughtId.value = thoughtid;
      showThought.value = true;
      showThoughtForm.value = true;
      topicForThoughtForm.value = projectStore.getFetchedTopic(topicid);
      scrollPosition.value = window.pageYOffset;
    }

    function closeThought(params = {}) {
      let force = false;
      if (params['force'] === true) {
        force = true;
      }
      let closeThought = true;

      if (showThoughtForm.value && !force) {
        // double check if we should close a form
        closeThought = confirmClosingForm();
      }

      if (closeThought) {
        // Why false? See MapProject.vue in the same method
        deleteHashParam('thought_id', false);
        openedThoughtId.value = null;
        showThought.value = false;
        showThoughtForm.value = false;
        topicForThoughtForm.value = null;
        if (scrollPosition.value !== null) {
          nextTick(function () {
            window.scrollTo(0, scrollPosition.value);
            scrollPosition.value = null;
          });
        }
      }
      clearFeedback();
    }

    function cancelThoughtForm() {
      if (openedThoughtId.value) {
        // go back to detail view without form
        showThoughtForm.value = false;
        topicForThoughtForm.value = null;
      } else {
        // force close create form entirely (go back to list)
        closeThought(true);
      }
      clearFeedback();
    }

    function openCreateThoughtForm(event) {
      //Check if user is logged in
      if (canCreateThoughtAnyTopic.value) {
        //Show create thought when user is logged in
        showThought.value = true;
        doShowThoughtForm();
        openedThoughtId.value = null;
        scrollPosition.value = window.pageYOffset;
        clearFeedback();
      } else if (canCreateThoughtAnyTopicAfterLogin.value) {
        loginOP.value.show(event);
      } else {
        console.error('error: cannot create thought, but button visible/clicked');
      }
    }

    function selectedTopicForForm(topic) {
      topicForThoughtForm.value = topic;
    }

    function onFeedbackFromThought(feedback) {
      //new thought was created, go back to list view
      if (feedback.newThought || !openedThoughtId.value) {
        closeThought({ force: true });
      }
      //thought was edited, go back to thought-detail
      if (!feedback.newThought && openedThoughtId.value) {
        openThought(openedThoughtId.value);
      }

      feedbackMessage.message = feedback.message;
      feedbackMessage.type = feedback.type;
    }

    // WATCHES
    // check hashparams here
    watch(route, () => {
      const thought_id = parseInt(route.query.thought_id);
      if (thought_id && openedThoughtId.value != thought_id) {
        openThought(thought_id);
      }

      const topicid = route.query.topic_id || route.query.topicid;
      if (route.query.topic_id) {
        // Switch to topicid and remove topic_id
        // deleteHashParam("topic_id");
        setHashParams({ topicid: topicid, topic_id: null });
      }
      if (topicid) {
        // Chosen topic will be set in `watch(projectStore.getState().topics)`
        filteredTopicIdFromHash.value = topicid;
      }
    });
    // watch on project in store to setup filter values after project was fetched
    watch(
      () => projectStore.getState().project,
      (project, oldProject) => {
        if (project !== null && project.id === props.projectid) {
          // If at least 1 topic has votetype = weighted_support, we need to add the weight sort options
          const isWeightedSupportType =
            project.topics && project.topics.find(x => x.votetype == 'weighted_support') != undefined;
          const sortOpts = getSortOptions({
            votetype: isWeightedSupportType ? 'weighted_support' : 'support',
            default_sort: 'random',
            commentcount: 2,
            new_comments: false,
          });
          defaultFilterFields.value.sort.default_value = sortOpts.default;
          defaultFilterFields.value.sort.values = sortOpts.values;

          if (project.config) {
            const weightField = getThoughtWeightField(project.topics, project.config.weighted_support_point_map);
            if (weightField != null) {
              defaultFilterFields.value.vote_weight = weightField;
            }
          }

          if (project.topics && project.topics.length > 1) {
            const options = [];
            project.topics.forEach(t => {
              options.push({
                id: t.id + '',
                ordering: 1,
                text: t.title,
              });
            });
            // Add discussion selection to project filter
            defaultFilterFields.value.topicid = {
              name: topicLabel(),
              type: 'valuelist',
              default_value: 0,
              visible: true,
              values: options,
              allowClear: true,
            };
          }
        }

        if (!userStore.getState().authToken && process.env.VUE_APP_ENABLE_VIRTUAL_USER) {
          // try to init/get virtual user for eventual anonymous votes/comments/thoughts
          getVirtualUser()
            .then(success => {
              // console.log('initialized virtual user');
            })
            .catch(e => {
              console.log('error getting virtual user');
            });
        }
      }
    );
    watch(
      () => projectStore.getState().topics,
      (newTopics, oldTopics) => {
        if (newTopics !== null && newTopics.length > 0) {
          topics.value = projectStore.getTopics();

          // TODO
          // [x] init attribute fields from entire project
          // [ ] init sort options from entire project
          const topic_sortOptions = {};
          filterAttributesDependency.value = { topicid: {} };

          // Per T18783: use chosen topic if it's defined
          let filteredTopic = null;
          if (filteredTopicIdFromHash.value > 0) {
            filteredTopic = newTopics.find(t => t.id == filteredTopicIdFromHash.value);
          }

          // update attribute fields for filter
          newTopics.forEach(t => {
            filterAttributesDependency.value.topicid[t.id] = [];

            // assign each attribute to its respective topic dependency
            if (t.attributes) {
              t.attributes.forEach(el => {
                if (toBool(el.relation_data.show_in_filter)) {
                  // clone element to be able to edit it for filter
                  const elCopy = JSON.parse(JSON.stringify(el));
                  elCopy.filter_name = 'attribute' + el.id;
                  elCopy.visible = true;
                  if (!attributeFilterFields.value.find(attr => attr.id === elCopy.id)) {
                    attributeFilterFields.value.push(elCopy);
                  }
                  filterAttributesDependency.value.topicid[t.id].push(elCopy);
                }
              });

              const sortOpts = getSortOptions({
                votetype: t.votetype,
                default_sort: t.default_sort,
                commentcount: t.commentcount,
                new_comments: t.new_comments,
              });
              topic_sortOptions[t.id] = sortOpts;
            }
          });

          const sortOptions = Object.entries(topic_sortOptions);
          // If a topic is preselected, this would have the filtered topic's sort option
          const sortOptionOnPreselectedTopic = filteredTopic ? sortOptions.find(x => x[0] == filteredTopic.id) : null;

          let topicid = null;
          if (sortOptionOnPreselectedTopic) {
            // sortOptionOnPreselectedTopic should be [ <topicid>, <hash-of-sort-options> ]
            // Per T18783, if there is only one entry for sort options, use that
            // Example: When it is filtered by topicid through hash param
            defaultFilterFields.value.sort.values = sortOptionOnPreselectedTopic[1].values;
            defaultFilterFields.value.sort.default_value = sortOptionOnPreselectedTopic[1].default;
            // Set chosen topicid
            topicid = sortOptions[0][0];
          }

          const filterParams = {
            sort: defaultFilterFields.value.sort.default_value,
            topicid: topicid,
            projectid: props.projectid,
          };
          // set default values for filter
          filterStore.setDefaultValues(filterParams);
          // Update the thought filter value for filtering
          thoughtStore.updateThoughtFilter(filterParams);
          // Reload the thoughts anyway
          // T22381: Don't reload thoughts! Let ThoughtFilter reloads them since filter values can come from hash param
          // thoughtStore.reloadThoughts();

          // TODO figure out how to define sort options over multiple topics
        }
      }
    );

    // watch(() =>  userStore.getState().user, (newUser,oldUser) => {
    //     // refresh project/topics in store when user is logged-in/out
    //     if((newUser == null && oldUser ) || (newUser && oldUser == null) || (newUser?.id != oldUser?.id )){
    //         projectStore.setProjectId(props.projectid);
    //     }
    // })
    /**
     * +------------------------+
     * |    Lifecycle hook      |
     * +------------------------+
     */
    onBeforeMount(() => {
      domainStore.setDomainId(props.domainid);
      projectStore.setProjectId(props.projectid).then(proj => {
        if (proj.config && 'thought_list_rows' in proj.config) {
          thoughtStore.setRows(parseInt(proj.config.thought_list_rows));
        }
        projectConfigLoaded.value = true;
      });
    });

    return {
      // refs
      filterAttributesDependency,
      attributeFilterFields,
      defaultFilterFields,
      openedThoughtId,
      showThoughtForm,
      showThought,
      loginOP,
      projectWrapper,
      wrapperConfig,
      wrapperActions,
      topicForThoughtForm,
      topics,
      projectConfigLoaded,
      // computed vars
      project,
      isLoggedIn,
      showDetailedThought,
      feedbackMessageSeverity,
      thoughtFormTitleLabel,
      thoughtFormDescriptionLabel,
      canCreateThoughtAnyTopic,
      canCreateThoughtAnyTopicAfterLogin,
      topicsForThoughts,
      // methods
      canCreateThought,
      canCreateThoughtAfterLogin,
      openThought,
      closeThought,
      editThought,
      cancelThoughtForm,
      openCreateThoughtForm,
      onFeedbackFromThought,
      selectedTopicForForm,
      domainStore,
      // Vars
      feedbackMessage,
      domain,
    };
  },

  components: {
    OverlayPanel,
    Login,
    ThoughtFilter,
    ThoughtList,
    WdwDynbox,
    ThoughtWrapper,
    ThoughtForm,
    ThoughtFormChooseTopic,
    Message,
  },
};
</script>

<style scoped>
#project-wrapper {
  scroll-margin-top: 100px;

  /* required so scroll-margin works in chrome */
  overflow: initial;
}
</style>
